import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Backpack } from 'lucide-react';
import { detectUserCurrency } from '../utils/locale';
import { useTranslation } from 'react-i18next';

const OnboardingModal = ({ open, onComplete }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [totalBudget, setTotalBudget] = useState('');
  const [tripDuration, setTripDuration] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [destination, setDestination] = useState('');

  // Auto-detect currency on mount
  useEffect(() => {
    const detectedCurrency = detectUserCurrency();
    setCurrency(detectedCurrency);
  }, []);

  const handleComplete = () => {
    const dailyBudget = totalBudget && tripDuration ? (parseFloat(totalBudget) / parseInt(tripDuration)).toFixed(2) : '0';
    
    onComplete({
      totalBudget: parseFloat(totalBudget),
      tripDuration: parseInt(tripDuration),
      dailyBudget: parseFloat(dailyBudget),
      currency,
      destination
    });
  };

  const canProceed = () => {
    if (step === 1) return totalBudget && parseFloat(totalBudget) > 0;
    if (step === 2) return tripDuration && parseInt(tripDuration) > 0;
    if (step === 3) return destination.trim().length > 0;
    return true;
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent data-testid="onboarding-modal" className="bg-card border-border max-w-md" hideClose={step < 4}>
        <DialogHeader>
          <div className="flex items-center justify-center gap-3 mb-4">
            <img src="/logo-icon.png" alt="" className="h-12 w-auto" data-testid="onboarding-logo" />
            <span className="text-xl font-heading font-bold text-foreground">Wanderer's<br /><span className="text-primary">Wallet</span></span>
          </div>
          <DialogTitle className="text-3xl font-heading text-center">
            {step === 1 && 'Welcome to Wanderer\'s Wallet'}
            {step === 2 && 'Trip Duration'}
            {step === 3 && 'Where are you going?'}
            {step === 4 && 'All Set!'}
          </DialogTitle>
          <p className="text-muted-foreground text-center mt-2">
            {step === 1 && 'Let\'s protect your ability to keep traveling'}
            {step === 2 && 'How long is your trip?'}
            {step === 3 && 'Tell us your destination'}
            {step === 4 && 'Review your budget before starting'}
          </p>
        </DialogHeader>

        <div className="space-y-6 py-6">
          {/* Step 1: Total Budget */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="total-budget" className="text-lg">Total Trip Budget</Label>
                <Input
                  id="total-budget"
                  data-testid="onboarding-total-budget"
                  type="number"
                  step="0.01"
                  placeholder="1500"
                  value={totalBudget}
                  onChange={(e) => setTotalBudget(e.target.value)}
                  className="bg-secondary/50 border-transparent text-3xl h-16 text-center font-bold"
                  autoFocus
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="currency">Currency</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger data-testid="onboarding-currency" id="currency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    <SelectItem value="USD">USD - United States</SelectItem>
                    <SelectItem value="EUR">EUR - European Union</SelectItem>
                    <SelectItem value="GBP">GBP - United Kingdom</SelectItem>
                    <SelectItem value="JPY">JPY - Japan</SelectItem>
                    <SelectItem value="AUD">AUD - Australia</SelectItem>
                    <SelectItem value="CAD">CAD - Canada</SelectItem>
                    <SelectItem value="CHF">CHF - Switzerland</SelectItem>
                    <SelectItem value="CNY">CNY - China</SelectItem>
                    <SelectItem value="SEK">SEK - Sweden</SelectItem>
                    <SelectItem value="NZD">NZD - New Zealand</SelectItem>
                    <SelectItem value="MXN">MXN - Mexico</SelectItem>
                    <SelectItem value="SGD">SGD - Singapore</SelectItem>
                    <SelectItem value="HKD">HKD - Hong Kong</SelectItem>
                    <SelectItem value="NOK">NOK - Norway</SelectItem>
                    <SelectItem value="KRW">KRW - South Korea</SelectItem>
                    <SelectItem value="TRY">TRY - Turkey</SelectItem>
                    <SelectItem value="INR">INR - India</SelectItem>
                    <SelectItem value="RUB">RUB - Russia</SelectItem>
                    <SelectItem value="BRL">BRL - Brazil</SelectItem>
                    <SelectItem value="ZAR">ZAR - South Africa</SelectItem>
                    <SelectItem value="THB">THB - Thailand</SelectItem>
                    <SelectItem value="IDR">IDR - Indonesia</SelectItem>
                    <SelectItem value="MYR">MYR - Malaysia</SelectItem>
                    <SelectItem value="PHP">PHP - Philippines</SelectItem>
                    <SelectItem value="VND">VND - Vietnam</SelectItem>
                    <SelectItem value="CZK">CZK - Czech Republic</SelectItem>
                    <SelectItem value="PLN">PLN - Poland</SelectItem>
                    <SelectItem value="AED">AED - United Arab Emirates</SelectItem>
                    <SelectItem value="ILS">ILS - Israel</SelectItem>
                    <SelectItem value="ARS">ARS - Argentina</SelectItem>
                    <SelectItem value="CLP">CLP - Chile</SelectItem>
                    <SelectItem value="COP">COP - Colombia</SelectItem>
                    <SelectItem value="PEN">PEN - Peru</SelectItem>
                    <SelectItem value="EGP">EGP - Egypt</SelectItem>
                    <SelectItem value="DKK">DKK - Denmark</SelectItem>
                    <SelectItem value="HUF">HUF - Hungary</SelectItem>
                    <SelectItem value="ISK">ISK - Iceland</SelectItem>
                    <SelectItem value="MAD">MAD - Morocco</SelectItem>
                    <SelectItem value="TWD">TWD - Taiwan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Step 2: Trip Duration */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="trip-duration" className="text-lg">Trip Duration (days)</Label>
                <Input
                  id="trip-duration"
                  data-testid="onboarding-trip-duration"
                  type="number"
                  placeholder="30"
                  value={tripDuration}
                  onChange={(e) => setTripDuration(e.target.value)}
                  className="bg-secondary/50 border-transparent text-3xl h-16 text-center font-bold"
                  autoFocus
                />
              </div>

              {totalBudget && tripDuration && (
                <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 text-center">
                  <p className="text-sm text-muted-foreground">Your daily budget will be</p>
                  <p className="text-2xl font-bold text-primary mt-1">
                    ${(parseFloat(totalBudget) / parseInt(tripDuration)).toFixed(2)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">per day</p>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Destination */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="destination" className="text-lg">Destination</Label>
                <Input
                  id="destination"
                  data-testid="onboarding-destination"
                  placeholder="Bangkok, Thailand"
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  className="bg-secondary/50 border-transparent text-xl h-14 text-center"
                  autoFocus
                />
              </div>
            </div>
          )}

          {/* Step 4: Summary */}
          {step === 4 && (
            <div className="space-y-4">
              <div className="bg-gradient-to-br from-card to-secondary/50 border border-primary/20 rounded-lg p-6 space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Budget:</span>
                  <span className="font-bold text-foreground">{currency} {parseFloat(totalBudget).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Trip Duration:</span>
                  <span className="font-bold text-foreground">{tripDuration} days</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Daily Budget:</span>
                  <span className="font-bold text-primary">
                    {currency} {(parseFloat(totalBudget) / parseInt(tripDuration)).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Destination:</span>
                  <span className="font-bold text-foreground">{destination}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex gap-3">
          {step > 1 && step < 4 && (
            <Button
              data-testid="onboarding-back-button"
              onClick={() => setStep(step - 1)}
              variant="outline"
              className="flex-1 rounded-full border-border"
            >
              Back
            </Button>
          )}
          
          {step < 4 && (
            <Button
              data-testid="onboarding-next-button"
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              className="flex-1 rounded-full bg-primary text-primary-foreground"
            >
              {step === 3 ? 'Review' : 'Next'}
            </Button>
          )}

          {step === 4 && (
            <Button
              data-testid="onboarding-complete-button"
              onClick={handleComplete}
              className="w-full rounded-full bg-primary text-primary-foreground h-14 text-lg font-semibold"
            >
              Start Tracking
            </Button>
          )}
        </div>

        {/* Progress Indicator */}
        <div className="flex justify-center gap-2 pt-4">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className={`h-2 w-2 rounded-full transition-all duration-300 ${
                i <= step ? 'bg-primary w-6' : 'bg-muted'
              }`}
            />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingModal;